import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Trash2, Mail, CheckCircle2, Loader2 } from "lucide-react";

const GOLD = "#C9A24B";
const CREAM = "#F6F3EC";
const INK = "#1C1A17";
const EMAIL = "infodogana2.0@gmail.com";

const TYPES = [
  { v: "cancellazione_dati", l: "Cancellazione dei dati personali" },
  { v: "anonimizzazione_crm", l: "Cancellazione/anonimizzazione del profilo CRM" },
  { v: "revoca_marketing", l: "Revoca dei consensi marketing" },
  { v: "stop_whatsapp", l: "Cessazione comunicazioni WhatsApp promozionali" },
];

function useMeta(title, desc) {
  useEffect(() => {
    document.title = title;
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.setAttribute("name", "description"); document.head.appendChild(m); }
    const prev = m.getAttribute("content");
    m.setAttribute("content", desc);
    return () => { if (prev) m.setAttribute("content", prev); };
  }, [title, desc]);
}

export default function DataDeletion() {
  useMeta("Richiesta di eliminazione dei dati · Dogana 2.0", "Richiedi la cancellazione dei tuoi dati personali, l'anonimizzazione del profilo CRM, la revoca dei consensi marketing o lo stop alle comunicazioni WhatsApp di Dogana 2.0.");
  const [form, setForm] = useState({ name: "", phone: "", email: "", request_type: "cancellazione_dati", notes: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!form.name.trim() || (!form.phone.trim() && !form.email.trim())) {
      toast.error("Inserisci il nome e almeno un contatto (telefono o email)");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/public/data-deletion-request", form);
      setDone(true);
      toast.success(data.message || "Richiesta ricevuta");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: CREAM, fontFamily: "'Outfit', sans-serif" }} data-testid="data-deletion-page">
      <div className="max-w-xl mx-auto px-5 py-10">
        <div className="flex items-center gap-2" style={{ color: GOLD }}>
          <Trash2 className="w-6 h-6" />
          <span className="text-xs uppercase tracking-[0.2em] font-semibold">Dogana 2.0</span>
        </div>
        <h1 className="mt-3 text-3xl md:text-4xl font-bold" style={{ color: INK }}>Richiesta di eliminazione dei dati</h1>
        <p className="mt-3 text-[15px] leading-relaxed text-black/70">
          Puoi chiedere in qualsiasi momento la cancellazione dei tuoi dati personali, la cancellazione o anonimizzazione del profilo CRM, la revoca dei consensi marketing e la cessazione delle comunicazioni WhatsApp promozionali.
        </p>

        <div className="mt-6 rounded-2xl border border-black/10 bg-white p-5">
          <h2 className="font-semibold" style={{ color: INK }}>1. Via email</h2>
          <p className="mt-1 text-[15px] text-black/70">Scrivi a <a className="underline" href={`mailto:${EMAIL}?subject=Richiesta eliminazione dati`} style={{ color: "#8a6d2b" }}>{EMAIL}</a> indicando il tipo di richiesta e un recapito per la verifica.</p>
        </div>

        <div className="mt-5 rounded-2xl border border-black/10 bg-white p-5">
          <h2 className="font-semibold" style={{ color: INK }}>2. Oppure compila il modulo</h2>
          {done ? (
            <div className="mt-4 flex items-start gap-3 rounded-xl bg-emerald-50 border border-emerald-200 p-4" data-testid="deletion-success">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
              <p className="text-[15px] text-emerald-800">Abbiamo ricevuto la richiesta. Verrà verificata prima dell'esecuzione.</p>
            </div>
          ) : (
            <div className="mt-4 space-y-3">
              <div>
                <Label className="text-sm">Nome *</Label>
                <Input value={form.name} onChange={set("name")} placeholder="Il tuo nome" data-testid="ddr-name" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">Telefono</Label>
                  <Input value={form.phone} onChange={set("phone")} placeholder="+39..." data-testid="ddr-phone" />
                </div>
                <div>
                  <Label className="text-sm">Email</Label>
                  <Input value={form.email} onChange={set("email")} placeholder="nome@email.it" data-testid="ddr-email" />
                </div>
              </div>
              <div>
                <Label className="text-sm">Tipo di richiesta</Label>
                <select value={form.request_type} onChange={set("request_type")} data-testid="ddr-type"
                  className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
                  {TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-sm">Note (facoltative)</Label>
                <Input value={form.notes} onChange={set("notes")} placeholder="Dettagli aggiuntivi" data-testid="ddr-notes" />
              </div>
              <p className="text-xs text-black/50">Non inserire dati sensibili. La richiesta viene registrata e verificata manualmente: nessuna cancellazione è automatica o immediata.</p>
              <Button onClick={submit} disabled={busy} className="w-full rounded-full h-11 font-semibold"
                style={{ backgroundColor: INK, color: CREAM }} data-testid="ddr-submit">
                {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Invia richiesta"}
              </Button>
            </div>
          )}
        </div>

        <footer className="mt-12 pt-6 border-t border-black/10 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
          <Link to="/privacy" className="underline" style={{ color: INK }} data-testid="link-to-privacy">Privacy Policy</Link>
          <Link to="/eliminazione-dati" className="underline" style={{ color: INK }}>Eliminazione dati</Link>
          <a href={`mailto:${EMAIL}`} className="inline-flex items-center gap-1 text-black/60"><Mail className="w-4 h-4" /> {EMAIL}</a>
        </footer>
      </div>
    </div>
  );
}
